import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import Auth from "util/auth";
import { Timers } from "util/timers";

/**
 * リトライ設定
 */
export type RetryConfig = {
  retries?: number;
  delayMills?: number;
};

/**
 * 認証設定
 */
export type AuthConfig = {
  /** MaaS基盤のAPIキー */
  apiKey?: string;
};

/**
 * デフォルトのリトライ設定。
 * 実際に適用する設定は、本モジュールを利用する側で制御することができます。
 *
 */
const DEFAULT_RETRY_CONFIG = Object.freeze({
  retries: 0, // リトライ回数。
  delayMills: 100, // リトライ前に設ける遅滞時間(ミリ秒数)。Lambdaのタイムアウト(デフォルトは3秒)に留意。
});

/**
 * デフォルトのリクエスト設定。
 * 実際に適用する設定は、本モジュールを利用する側で制御することができます。
 *
 */
const DEFAULT_REQUEST_CONFIG = Object.freeze({
  timeout: 30 * 1000, // リクエストタイムアウト(ミリ秒数)。Lambdaのタイムアウト(デフォルトは3秒)に留意。
});

/**
 * AxiosベースのHTTPクライアント。
 *
 * - 本クラスはイミュータブルです。リトライ設定、リクエスト設定の変更では新しいオブジェクトを返します。
 * - JSON APIとの通信に使用することを想定しています。
 *   - 本クラスは、Formデータをリクエストするためのショートハンドを提供していません。
 *   - Formデータリクエストの実装例は {@link https://github.com/axios/axios#using-applicationx-www-form-urlencoded-format GitHub Axios} を参照してください。
 *   - また、本クラスは、マルチパートリクエストの関数を提供していません。
 *   - Axiosの"postForm","putForm","patchForm"が、"multipart/form-data"用の関数です。
 * - AxiosのAPIと同様、このクラスの各関数の引数"data"はリクエストボディ。"AxiosRequestConfig.params"はクエリストリングを指しています。
 * @see {@link https://github.com/axios/axios GitHub Axios}
 */
export class Http {
  /**
   * デフォルト設定のHTTPクライアント
   */
  static #default = new Http(DEFAULT_RETRY_CONFIG, DEFAULT_REQUEST_CONFIG);

  /**
   * デフォルト設定のHTTPクライアント
   */
  static get default() {
    return Http.#default;
  }

  /**
   * リトライ設定
   */
  readonly #retryConfig: RetryConfig = {};

  /**
   * リクエスト設定
   */
  readonly #requestConfig: AxiosRequestConfig<any> = {};

  /**
   * コンストラクタ
   * @param retryConfig リトライ設定
   * @param requestConfig リクエスト設定
   */
  constructor(retryConfig: RetryConfig, requestConfig: AxiosRequestConfig<any>) {
    this.#retryConfig = Object.freeze(retryConfig);
    this.#requestConfig = Object.freeze(requestConfig);
  }

  /**
   * 現在の設定を元に、リトライ設定を変更した新しいHTTPクライアントを作成します
   * @param config リトライ設定
   * @returns 新しく作成したHTTPクライアント
   */
  alterRetry(config: RetryConfig) {
    const altered = { ...this.#retryConfig, ...config };
    return new Http(altered, this.#requestConfig);
  }

  /**
   * 現在の設定を元に、リクエスト設定を変更した新しいHTTPクライアントを作成します
   * @param config リクエスト設定
   * @returns 新しく作成したHTTPクライアント
   */
  alterRequest(config: AxiosRequestConfig<any>) {
    const altered = { ...this.#requestConfig, ...config };
    return new Http(this.#retryConfig, altered);
  }

  /**
   * 現在の設定を元に、認証情報を設定した新しいHTTPクライアントを作成します
   * @param authConfig 認証情報
   * @param config リクエスト設定
   * @returns 新しく作成したHTTPクライアント
   */
  alterAuth(authConfig: AuthConfig, config?: AxiosRequestConfig<any>) {
    const altered = { ...this.#requestConfig, ...config };

    // APIキーは指定された値がなければ取得して設定する
    const apiKeyHeader = { ...altered.headers, ...{ "x-api-key": "" } };
    apiKeyHeader["x-api-key"] = authConfig.apiKey ?? Auth.apikey();
    altered.headers = apiKeyHeader;

    return new Http(this.#retryConfig, altered);
  }

  /**
   * GETリクエストを送信します
   *
   * - 保持するリトライ設定、リクエスト設定に従って処理を実施します。
   * - リクエストパラメーターの指定は、理由がない限り、オブジェクト形式を使用してください。(下記例を参照)
   * @example
   * await http.get("https://httpbin.org/get"); // リソースをGET。
   * await http.get("https://httpbin.org/get", { params: { foo: 1, bar: "a" } }); // リクエストパラメーターはオブジェクト形式で指定する。
   * await http.get("https://httpbin.org/get?foo=1&bar=a"); // URLへのクエリストリング付与は避ける。
   * @param url HTTPリクエストの送信先URL
   * @param extraConfig この処理にのみ適用する追加のリクエスト設定
   * @returns HTTPレスポンス
   */
  async get<T = any>(
    url: string,
    extraConfig?: AxiosRequestConfig<any>
  ): Promise<AxiosResponse<T>> {
    const config = { ...this.#requestConfig, ...extraConfig };
    return Http.#exec(() => axios.get<T>(url, config), this.#retryConfig);
  }

  /**
   * POSTリクエストを送信します。
   *
   * - 保持するリトライ設定、リクエスト設定に従って処理を実施します。
   * - デフォルトでは、指定のデータをJSONに変換して送信します。
   * @example
   * await http.post("https://httpbin.org/post", { foo: 1, bar: "a" }); // デフォルトでは、{"foo":1,"bar":"a"} をPOST送信する。
   * @param url HTTPリクエストの送信先URL。
   * @param data リクエストボディとして送信するデータ。
   * @param extraConfig この処理にのみ適用する追加のリクエスト設定。
   * @returns HTTPレスポンス。
   */
  async post<T = any>(
    url: string,
    data: any,
    extraConfig?: AxiosRequestConfig<any>
  ): Promise<AxiosResponse<T>> {
    const config = { ...this.#requestConfig, ...extraConfig };
    return Http.#exec(() => axios.post<T>(url, data, config), this.#retryConfig);
  }

  /**
   * PUTリクエストを送信します。
   *
   * - 保持するリトライ設定、リクエスト設定に従って処理を実施します。
   * - デフォルトでは、指定のデータをJSONに変換して送信します。
   * @example
   * await http.put("https://httpbin.org/put", { foo: 1, bar: "a" }); // デフォルトでは、{"foo":1,"bar":"a"} をPUT送信する。
   * @param url HTTPリクエストの送信先URL。
   * @param data リクエストボディとして送信するデータ。
   * @param extraConfig この処理にのみ適用する追加のリクエスト設定。
   * @returns HTTPレスポンス。
   */
  async put<T = any>(url: string, data: any, extraConfig?: AxiosRequestConfig<any>) {
    const config = { ...this.#requestConfig, ...extraConfig };
    return Http.#exec(() => axios.put<T>(url, data, config), this.#retryConfig);
  }

  /**
   * PATCHリクエストを送信します。
   *
   * - 保持するリトライ設定、リクエスト設定に従って処理を実施します。
   * - デフォルトでは、指定のデータをJSONに変換して送信します。
   * @example
   * await http.patch("https://httpbin.org/patch", { foo: 1, bar: "a" }); // デフォルトでは、{"foo":1,"bar":"a"} をPATCH送信する。
   * @param url HTTPリクエストの送信先URL。
   * @param data リクエストボディとして送信するデータ。
   * @param extraConfig この処理にのみ適用する追加のリクエスト設定。
   * @returns HTTPレスポンス。
   */
  async patch<T = any>(url: string, data: any, extraConfig?: AxiosRequestConfig<any>) {
    const config = { ...this.#requestConfig, ...extraConfig };
    return Http.#exec(() => axios.patch<T>(url, data, config), this.#retryConfig);
  }

  /**
   * DELETEリクエストを送信します。
   *
   * - 保持するリトライ設定、リクエスト設定に従って処理を実施します。
   * - リクエストパラメーターの指定は、理由がない限り、オブジェクト形式を使用してください。(下記例を参照)
   * @example
   * await http.delete("https://httpbin.org/delete"); // リソースをDELETE。
   * await http.delete("https://httpbin.org/delete?foo=1&bar=a"); // リクエストパラメーターはオブジェクト形式で指定する。
   * await http.delete("https://httpbin.org/delete", { params: { foo: 1, bar: "a" } }); // URLへのクエリストリング付与は避ける。
   * @param url HTTPリクエストの送信先URL。
   * @param extraConfig この処理にのみ適用する追加のリクエスト設定。
   * @returns HTTPレスポンス。
   */
  async delete<T = any>(url: string, extraConfig?: AxiosRequestConfig<any>) {
    const config = { ...this.#requestConfig, ...extraConfig };
    return Http.#exec(() => axios.delete<T>(url, config), this.#retryConfig);
  }

  /**
   * HTTPリクエストを実行します
   * @param callback 実行するリクエスト処理
   * @param retryConfig リトライ設定
   * @returns HTTPレスポンス
   */
  static async #exec(callback: () => Promise<AxiosResponse<any>>, retryConfig: RetryConfig) {
    const maxTries = retryConfig.retries ?? DEFAULT_RETRY_CONFIG.retries;
    const delayMillis = retryConfig.delayMills ?? DEFAULT_RETRY_CONFIG.delayMills;
    let tried = 0;

    // ここでのwhile-trueは問題ないことを確認済み
    // eslint-disable-next-line no-constant-condition
    while (true) {
      try {
        // 逐次実行で意図どおり。
        // eslint-disable-next-line no-await-in-loop
        return await callback();
      } catch (error) {
        tried += 1;
        if (tried >= maxTries) {
          throw error;
        }
        if (!this.#isRetriableError(error)) {
          throw error;
        }
        // eslint-disable-next-line no-await-in-loop
        await Timers.delay(delayMillis);
      }
    }
  }

  /**
   * リトライ失敗時エラー
   * @param error エラー内容
   * @returns リトライするエラーの場合true
   */
  static #isRetriableError(error: unknown) {
    if (!axios.isAxiosError(error)) {
      return false;
    }
    const isRequestTimeout = error.code === "ECONNABORTED";
    if (isRequestTimeout) {
      return false;
    }
    const httpStatus = error.response?.status;
    if (httpStatus === 503) {
      // 503 Service Unavailable
      return true;
    }
    return false;
  }
}

export default Http;
